import React, { useEffect, useMemo } from 'react';

import { groupBy, sortBy } from 'lodash';
import { Controller, useForm } from 'react-hook-form';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useMutationImportToOpcXerFile } from 'api/mutations/importToOpcXerFile.mutation';
import { useQueryGetOpcProjectsList } from 'api/queries/getOpcProjectsList.query';

import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { Select } from 'components/DesignSystem/Select';
import classes from 'components/widgets/RenderLibraryTable/components/RenameVersionModal/RenameVersionModal.module.scss';

export interface FormProps {
    projectOpcId: number | null;
    workspaceOpcId: number | null;
}

export const UploadToOpcModal = ({ open, onClose, isLoading, disabled, id }) => {
    const { handleSnackBar } = useCustomSnackBar();
    const { data: opcProjectsList } = useQueryGetOpcProjectsList();
    const { mutate } = useMutationImportToOpcXerFile();
    const { control, handleSubmit, watch, setValue } = useForm<FormProps>({
        defaultValues: {
            projectOpcId: null,
            workspaceOpcId: null,
        },
    });

    const opcWorkspaces = useMemo(() => {
        if (opcProjectsList) {
            const groups = groupBy(opcProjectsList, 'workspaceOpcId');
            const workspaces = Object.keys(groups).map((key) => ({
                value: Number(key),
                label: key,
            }));
            return sortBy(workspaces, 'label');
        }
        return [];
    }, [opcProjectsList]);

    const workspaceOpcId = watch('workspaceOpcId');

    useEffect(() => {
        if (opcWorkspaces.length > 0 && workspaceOpcId === null) {
            setValue('workspaceOpcId', opcWorkspaces[0].value, { shouldDirty: true });
        }
    }, [opcWorkspaces, setValue]);

    const opcProjects = useMemo(() => {
        if (opcProjectsList && workspaceOpcId) {
            return opcProjectsList
                .filter((item) => item.workspaceOpcId === workspaceOpcId)
                .map((item) => ({
                    value: item.projectOpcId,
                    label: item.projectOpcName,
                }));
        }
        return [];
    }, [opcProjectsList, workspaceOpcId]);

    useEffect(() => {
        if (workspaceOpcId && opcProjects.length > 0) {
            setValue('projectOpcId', opcProjects[0].value, { shouldDirty: true });
        }
    }, [opcProjects, setValue, workspaceOpcId]);

    const handleSave = (data: FormProps) => {
        const payload = {
            projectOpcId: data.projectOpcId,
            projectOpcName: opcProjects.find((item) => item.value === data.projectOpcId)?.label || '',
            workspaceOpcId: data.workspaceOpcId,
            metaDataId: id,
            opcOption: null,
        };
        mutate(payload, {
            onSuccess: () => {
                handleSnackBar('OPC XER file uploaded successfully', 'success');
                onClose();
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
        });
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={'Upload version to OPC'}
            actions={
                <>
                    <Button size={'large'} variant={'secondary'} onClick={onClose}>
                        Cancel
                    </Button>

                    <Button
                        type={'submit'}
                        size={'large'}
                        variant={'primary'}
                        isLoading={isLoading}
                        disabled={disabled}
                        onClick={handleSubmit(handleSave)}
                    >
                        Save
                    </Button>
                </>
            }
        >
            <div className={classes.container}>
                <form onSubmit={handleSubmit(handleSave)}>
                    <div>
                        <Controller
                            render={({ field }) => {
                                return (
                                    <Select
                                        {...field}
                                        options={opcWorkspaces}
                                        value={opcWorkspaces.find((item) => item.value === field.value) || null}
                                        onChange={(option) => field.onChange(option ? (option.value as number) : null)}
                                        placeholder={'Select OPC workspace'}
                                        label={'OPC workspaces'}
                                    />
                                );
                            }}
                            name={'workspaceOpcId'}
                            control={control}
                        />
                    </div>
                    <div>
                        <Controller
                            render={({ field }) => {
                                const value = opcProjects.find((item) => item.value === field.value) || null;
                                return (
                                    <Select
                                        {...field}
                                        options={opcProjects}
                                        value={value}
                                        onChange={(option) => {
                                            field.onChange(option ? (option.value as number) : null);
                                        }}
                                        placeholder={'Select OPC project'}
                                        label={'OPC projects'}
                                    />
                                );
                            }}
                            name={'projectOpcId'}
                            control={control}
                        />
                    </div>
                </form>
            </div>
        </Modal>
    );
};
